import { z } from 'zod';
import type { LiveStatus, PositionSource, StopKind, TrackingSource } from '../enums.js';

/** Live vehicle snapshot returned by GET /live/vehicles (Redis-backed). */
export interface LiveVehicle {
  id: string;
  plateAr: string;
  plateLatin: string | null;
  name: string | null;
  type: string;
  groupId: string | null;
  trackingSource: TrackingSource;
  vehicleStatus: 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE';
  driver: { id: string; fullName: string; phone: string } | null;
  device: { id: string; imei: string } | null;
  live: {
    status: LiveStatus;
    statusSince: string | null;
    activeSource: PositionSource | null;
    lastAt: string | null;
    lat: number | null;
    lon: number | null;
    heading: number | null;
    speedKph: number | null;
    ignition: boolean | null;
    odometerKm: number | null;
    fuelLevelPct: number | null;
    address: string | null;
    placeId: string | null;
    placeName: string | null;
    tripId: string | null;
    gsmSignal: number | null;
    extVoltageMv: number | null;
  };
}

export const HistoryQuerySchema = z
  .object({
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    /** Max points to return; the server downsamples evenly above this. */
    maxPoints: z.coerce.number().int().min(100).max(20_000).default(5000),
  })
  .refine((q) => Date.parse(q.to) > Date.parse(q.from), { message: 'to must be after from' })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 31 * 86_400_000, { message: 'range must be ≤ 31 days' });
export type HistoryQuery = z.infer<typeof HistoryQuerySchema>;

export interface HistoryPoint {
  at: string;
  lat: number;
  lon: number;
  speedKph: number | null;
  heading: number | null;
  ignition: boolean | null;
  source: PositionSource;
  distanceM: number;
}

export interface HistoryStop {
  id: string;
  kind: StopKind;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  lat: number;
  lon: number;
  placeId: string | null;
  placeName: string | null;
  address: string | null;
}

export interface HistoryDrive {
  id: string;
  startedAt: string;
  endedAt: string | null;
  distanceKm: number;
  durationSec: number | null;
  maxSpeedKph: number | null;
  idleSec: number;
  source: PositionSource;
}

export interface HistoryResponse {
  vehicleId: string;
  from: string;
  to: string;
  totalPoints: number;
  points: HistoryPoint[];
  stops: HistoryStop[];
  drives: HistoryDrive[];
  summary: { distanceKm: number; movingSec: number; stoppedSec: number; idleSec: number; maxSpeedKph: number };
}

/** Derived events emitted by the ingest pipeline to the `alerts` queue. */
export const DerivedEventSchema = z.object({
  tenantId: z.string().uuid(),
  vehicleId: z.string().uuid(),
  driverId: z.string().uuid().nullable(),
  tripId: z.string().uuid().nullable(),
  type: z.string(),
  occurredAt: z.string().datetime({ offset: true }),
  lat: z.number(),
  lon: z.number(),
  data: z.record(z.unknown()).default({}),
});
export type DerivedEvent = z.infer<typeof DerivedEventSchema>;
